import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Popover,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { tss } from 'tss-react/mui';
import AddIcon from '@mui/icons-material/Add';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  VariableExtractionPlanFormValue,
  VariableExtractionRootSchema,
  VariableExtractionSchemaNode,
} from '@features/Admin/Agent/utils/handoff-rule-mapper';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';

type UiType = 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object' | 'enum';
const TYPES: UiType[] = ['string', 'number', 'integer', 'boolean', 'array', 'object', 'enum'];
const ARRAY_ITEM_TOKEN = '[]';
const PATH_DELIMITER = '___path___';

type TreeEntry = {
  path: string[];
  pathKey: string;
  name: string;
  depth: number;
  node: VariableExtractionSchemaNode;
  isArrayItem?: boolean;
};

type VariableExtractionPlanEditorProps = {
  value?: VariableExtractionPlanFormValue;
  onChange: (value: VariableExtractionPlanFormValue) => void;
  disabled?: boolean;
  draggable?: boolean;
  labels?: {
    helperText?: string;
    emptyStateText?: string;
    variableNameHeader?: string;
    typeHeader?: string;
    requiredHeader?: string;
    actionsHeader?: string;
    variableNamePlaceholder?: string;
    editFieldDetailsAriaLabel?: string;
    descriptionLabel?: string;
    descriptionPlaceholder?: string;
    enumValuesLabel?: string;
    addPropertyLabel?: string;
  };
};

const defaultSchema = (): VariableExtractionRootSchema => ({
  type: 'object',
  properties: {},
  required: [],
  additionalProperties: false,
});

const useStyles = tss.create(({ theme }) => ({
  helpIcon: { fontSize: 16, color: theme.palette.text.secondary },
  panelHelpIcon: { fontSize: 16 },
  codeIcon: { fontSize: 16, color: theme.palette.text.secondary },
  jsonPreviewPaper: {
    padding: theme.spacing(1.5),
    maxHeight: 520,
    overflow: 'auto',
    backgroundColor: theme.palette.extendedGrey?.darkest ?? theme.palette.grey[900],
    color: theme.palette.primary.light,
  },
  jsonPreviewPre: { margin: 0, fontSize: 12 },
  editorSplit: { minHeight: 540 },
  treePaper: {
    width: '100%',
    padding: theme.spacing(1),
    overflowY: 'auto',
    [theme.breakpoints.up('md')]: { width: '44%' },
  },
  treeHeader: {
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  emptyTree: { minHeight: 320, textAlign: 'center' },
  treeRow: {
    minHeight: 36,
    display: 'grid',
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    gridTemplateColumns: '8px 1fr',
  },
  treeRowActive: { backgroundColor: theme.palette.primary.light },
  treeRowDepth1: { gridTemplateColumns: '22px 1fr' },
  treeRowDepth2: { gridTemplateColumns: '36px 1fr' },
  treeRowDepth3: { gridTemplateColumns: '50px 1fr' },
  treeRowDepth4: { gridTemplateColumns: '64px 1fr' },
  treeRowDepth5: { gridTemplateColumns: '78px 1fr' },
  treeRowDepth6: { gridTemplateColumns: '92px 1fr' },
  treeRowContent: { paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) },
  treeName: { flex: 1 },
  badgeString: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.grey[700],
    height: 20,
  },
  badgeNumber: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    height: 20,
  },
  badgeInteger: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    height: 20,
  },
  badgeBoolean: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
    height: 20,
  },
  badgeArray: {
    backgroundColor: alpha(theme.palette.chart[9], 0.12),
    color: theme.palette.chart[9],
    height: 20,
  },
  badgeObject: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.dark,
    height: 20,
  },
  badgeEnum: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.status.danger.foreground,
    height: 20,
  },
  addRootButton: {
    minHeight: 30,
    justifyContent: 'flex-start',
    borderStyle: 'dashed',
    color: theme.palette.text.secondary,
  },
  fieldPaper: {
    width: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: { width: '56%' },
  },
  fieldHeader: { marginBottom: theme.spacing(1.5) },
  pathLabel: {
    display: 'block',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginBottom: theme.spacing(0.75),
  },
  variableInput: { marginTop: theme.spacing(1) },
  addNestedButton: {
    marginTop: theme.spacing(1.25),
    marginBottom: theme.spacing(0.75),
    alignSelf: 'flex-start',
  },
  typeToggleGroup: {
    marginTop: theme.spacing(1.25),
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(0.75),
    width: '100%',
    border: 0,
    '& .MuiToggleButton-root': {
      minWidth: 0,
      margin: 0,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: `${theme.shape.borderRadius}px !important`,
      textTransform: 'none',
      minHeight: 44,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    '& .MuiToggleButtonGroup-grouped': {
      border: `1px solid ${theme.palette.divider} !important`,
      margin: 0,
    },
  },
  jsonAccordion: { border: 'none', marginTop: theme.spacing(1.5) },
  accordionDetails: { padding: 0 },
  jsonBox: {
    position: 'relative',
    borderTop: '1px solid',
    borderColor: theme.palette.border?.strong ?? theme.palette.divider,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.extendedGrey?.darkest ?? theme.palette.grey[900],
    maxHeight: 256,
    overflow: 'auto',
    padding: theme.spacing(1.25),
  },
  copyJsonButton: {
    position: 'sticky',
    top: 0,
    left: 'calc(100% - 24px)',
    zIndex: 1,
    color: theme.palette.extendedGrey?.neutralLight,
    padding: theme.spacing(0.5),
  },
  copyIcon: { fontSize: 14 },
  jsonFieldPre: {
    margin: 0,
    fontSize: 12,
    color: theme.palette.extendedGrey?.light,
  },
  shortcutsStack: { padding: theme.spacing(1.75), minWidth: 280 },
}));

type SchemaTreeRowProps = {
  pathKey: string;
  name: string;
  node: VariableExtractionSchemaNode;
  onSelect: (pathKey: string) => void;
  getBadgeClass: (type: UiType) => string;
  rowClassName: string;
  contentClassName: string;
  nameClassName: string;
};

const SchemaTreeRow: React.FC<SchemaTreeRowProps> = ({
  pathKey,
  name,
  node,
  onSelect,
  getBadgeClass,
  rowClassName,
  contentClassName,
  nameClassName,
}) => {
  const labelType = (
    node.type === 'string' && (node.enum ?? []).length > 0 ? 'enum' : node.type
  ) as UiType;

  return (
    <Box key={pathKey} onClick={() => onSelect(pathKey)} className={rowClassName}>
      <Box />
      <Stack direction="row" alignItems="center" spacing={0.5} className={contentClassName}>
        <Typography variant="body2" className={nameClassName}>
          {name}
        </Typography>
        <Chip size="small" label={labelType} className={getBadgeClass(labelType)} />
      </Stack>
    </Box>
  );
};

type SyntaxJsonColors = {
  key: string;
  string: string;
  literal: string;
};

const syntaxJson = (json: string, colors: SyntaxJsonColors) => {
  const escaped = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return escaped.replace(
    /("(?:\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+\-]?\d+)?)/g,
    (token) => {
      if (token.startsWith('"') && token.endsWith(':')) {
        return `<span style="color:${colors.key};">${token.slice(0, -1)}</span>:`;
      }
      if (token.startsWith('"')) {
        return `<span style="color:${colors.string};">${token}</span>`;
      }
      return `<span style="color:${colors.literal};">${token}</span>`;
    },
  );
};

const cloneSchema = (schema: VariableExtractionRootSchema) =>
  JSON.parse(JSON.stringify(schema)) as VariableExtractionRootSchema;

const pathToKey = (path: string[]) => path.join(PATH_DELIMITER);

const getNodeByPath = (
  root: VariableExtractionRootSchema,
  path: string[],
): VariableExtractionSchemaNode | undefined => {
  if (path.length === 0) return undefined;
  let current: VariableExtractionSchemaNode | undefined = root.properties?.[path[0]];
  if (!current) return undefined;

  for (const segment of path.slice(1)) {
    if (segment === ARRAY_ITEM_TOKEN) {
      if (current.type !== 'array' || !current.items) return undefined;
      current = current.items;
      continue;
    }
    if (current.type !== 'object') return undefined;
    current = current.properties?.[segment];
    if (!current) return undefined;
  }
  return current;
};

const getParentForPath = (
  root: VariableExtractionRootSchema,
  path: string[],
): {
  parentObject?: VariableExtractionRootSchema | VariableExtractionSchemaNode;
  parentArray?: VariableExtractionSchemaNode;
  key?: string;
  isArrayItem: boolean;
} => {
  if (path.length === 0) return { isArrayItem: false };
  const last = path[path.length - 1];
  if (last === ARRAY_ITEM_TOKEN) {
    const arrayNode = getNodeByPath(root, path.slice(0, -1));
    return {
      parentArray: arrayNode?.type === 'array' ? arrayNode : undefined,
      isArrayItem: true,
    };
  }

  if (path.length === 1) {
    return { parentObject: root, key: last, isArrayItem: false };
  }

  const parentNode = getNodeByPath(root, path.slice(0, -1));
  if (parentNode?.type !== 'object') return { isArrayItem: false };
  return { parentObject: parentNode, key: last, isArrayItem: false };
};

const asObjectNode = (node: VariableExtractionSchemaNode): VariableExtractionSchemaNode => {
  if (node.type === 'object') return node;
  return {
    type: 'object',
    properties: {},
    required: [],
    additionalProperties: false,
  };
};

const buildTreeEntries = (root: VariableExtractionRootSchema): TreeEntry[] => {
  const entries: TreeEntry[] = [];

  const walkObject = (
    properties: Record<string, VariableExtractionSchemaNode> | undefined,
    pathPrefix: string[],
    depth: number,
  ) => {
    for (const [name, node] of Object.entries(properties ?? {})) {
      const path = [...pathPrefix, name];
      entries.push({ path, pathKey: pathToKey(path), name, depth, node });

      if (node.type === 'object') {
        walkObject(node.properties, path, depth + 1);
        continue;
      }

      if (node.type === 'array' && node.items) {
        const itemPath = [...path, ARRAY_ITEM_TOKEN];
        entries.push({
          path: itemPath,
          pathKey: pathToKey(itemPath),
          name: 'item',
          depth: depth + 1,
          node: node.items,
          isArrayItem: true,
        });

        if (node.items.type === 'object') {
          walkObject(node.items.properties, itemPath, depth + 2);
        }
      }
    }
  };

  walkObject(root.properties, [], 0);
  return entries;
};

export const VariableExtractionPlanEditor: React.FC<VariableExtractionPlanEditorProps> = ({
  value,
  onChange,
  disabled = false,
  draggable = true,
  labels,
}) => {
  const theme = useTheme();
  const { classes, cx } = useStyles();
  const syntaxJsonColors = React.useMemo(
    () => ({
      key: theme.palette.extendedGrey?.neutralLight ?? theme.palette.text.secondary,
      string: theme.palette.success.main,
      literal: theme.palette.warning.main,
    }),
    [theme],
  );
  void draggable;
  const schema = value?.schema ?? defaultSchema();
  const [showJsonPreview, setShowJsonPreview] = React.useState(false);
  const [shortcutAnchor, setShortcutAnchor] = React.useState<HTMLElement | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [selectedFieldPathKey, setSelectedFieldPathKey] = React.useState<string | null>(null);
  const [variableNameDraft, setVariableNameDraft] = React.useState('');
  const treeEntries = React.useMemo(() => buildTreeEntries(schema), [schema]);
  const activeEntry =
    treeEntries.find((entry) => entry.pathKey === selectedFieldPathKey) ?? treeEntries[0];
  const activeFieldPath = activeEntry?.path;
  const activeFieldName = activeEntry?.name ?? null;
  const activeFieldNode = activeEntry?.node;
  const selectedType = activeFieldNode
    ? ((activeFieldNode.type === 'string' && (activeFieldNode.enum ?? []).length > 0
        ? 'enum'
        : activeFieldNode.type) as UiType)
    : 'string';
  const subtreeJson = activeFieldNode
    ? JSON.stringify({ [activeFieldName ?? 'field']: activeFieldNode }, null, 2)
    : '{}';

  React.useEffect(() => {
    setVariableNameDraft(activeFieldName ?? '');
  }, [activeFieldName, selectedFieldPathKey]);

  const addRootField = () => {
    const next = cloneSchema(schema);
    const existing = new Set(Object.keys(next.properties ?? {}));
    let i = existing.size + 1;
    let key = `field_${i}`;
    while (existing.has(key)) {
      i += 1;
      key = `field_${i}`;
    }
    next.properties[key] = { type: 'string' };
    onChange({ strict: true, schema: next });
    setSelectedFieldPathKey(pathToKey([key]));
  };

  const updateSelectedFieldName = (nextName: string) => {
    if (
      !activeFieldPath ||
      !activeFieldName ||
      activeFieldPath[activeFieldPath.length - 1] === ARRAY_ITEM_TOKEN
    )
      return;
    const trimmed = nextName.trim();
    if (!trimmed || trimmed === activeFieldName) return;

    const next = cloneSchema(schema);
    const { parentObject, key } = getParentForPath(next, activeFieldPath);
    if (!parentObject || !key || parentObject.type !== 'object') return;

    const props = parentObject.properties ?? {};
    if (props[trimmed]) return;

    const node = props[key];
    if (!node) return;
    delete props[key];
    props[trimmed] = node;
    parentObject.required = (parentObject.required ?? []).map((item) =>
      item === key ? trimmed : item,
    );

    onChange({ strict: true, schema: next });
    const renamedPath = [...activeFieldPath.slice(0, -1), trimmed];
    setSelectedFieldPathKey(pathToKey(renamedPath));
  };

  const updateSelectedFieldType = (nextType: UiType) => {
    if (!activeFieldPath || !activeFieldNode) return;
    const next = cloneSchema(schema);
    const { parentObject, parentArray, key, isArrayItem } = getParentForPath(next, activeFieldPath);
    if (!parentObject && !parentArray) return;

    let nextNode: VariableExtractionSchemaNode;
    if (nextType === 'enum') {
      nextNode = {
        ...activeFieldNode,
        type: 'string',
        enum: activeFieldNode.enum?.length ? activeFieldNode.enum : [''],
      };
    } else if (nextType === 'object') {
      nextNode = {
        type: 'object',
        properties: {},
        required: [],
        additionalProperties: false,
      };
    } else if (nextType === 'array') {
      nextNode = { type: 'array', items: { type: 'string' } };
    } else {
      nextNode = { type: nextType };
    }

    if (isArrayItem && parentArray) {
      parentArray.items = nextNode;
    } else if (parentObject && key && parentObject.type === 'object') {
      parentObject.properties = parentObject.properties ?? {};
      parentObject.properties[key] = nextNode;
    }

    onChange({ strict: true, schema: next });
  };

  const addNestedField = () => {
    if (!activeFieldPath || !activeFieldNode) return;

    const next = cloneSchema(schema);
    const selectedInNext = getNodeByPath(next, activeFieldPath);
    if (!selectedInNext) return;

    let container: VariableExtractionSchemaNode;
    let nextPathPrefix: string[];

    if (selectedInNext.type === 'object') {
      container = selectedInNext;
      nextPathPrefix = [...activeFieldPath];
    } else if (selectedInNext.type === 'array') {
      const ensuredItem = asObjectNode(
        selectedInNext.items ?? ({ type: 'object' } as VariableExtractionSchemaNode),
      );
      selectedInNext.items = ensuredItem;
      container = ensuredItem;
      nextPathPrefix = [...activeFieldPath, ARRAY_ITEM_TOKEN];
    } else {
      return;
    }

    container.properties = container.properties ?? {};
    container.required = container.required ?? [];

    const existing = new Set(Object.keys(container.properties));
    let i = existing.size + 1;
    let key = `field_${i}`;
    while (existing.has(key)) {
      i += 1;
      key = `field_${i}`;
    }

    container.properties[key] = { type: 'string' };
    onChange({ strict: true, schema: next });
    setSelectedFieldPathKey(pathToKey([...nextPathPrefix, key]));
  };

  const canAddNestedField =
    Boolean(activeFieldNode) &&
    (activeFieldNode?.type === 'object' || activeFieldNode?.type === 'array');
  const addNestedDisabledReason = (() => {
    if (disabled) return 'Editing is disabled';
    if (!activeFieldNode) return 'Select a field first';
    if (activeFieldNode.type !== 'object' && activeFieldNode.type !== 'array') {
      return 'Nested fields are available only for object or array types';
    }
    return '';
  })();
  const getBadgeClass = (type: UiType) => {
    switch (type) {
      case 'number':
        return classes.badgeNumber;
      case 'integer':
        return classes.badgeInteger;
      case 'boolean':
        return classes.badgeBoolean;
      case 'array':
        return classes.badgeArray;
      case 'object':
        return classes.badgeObject;
      case 'enum':
        return classes.badgeEnum;
      default:
        return classes.badgeString;
    }
  };
  const getDepthClass = (depth: number) => {
    if (depth <= 0) return '';
    if (depth === 1) return classes.treeRowDepth1;
    if (depth === 2) return classes.treeRowDepth2;
    if (depth === 3) return classes.treeRowDepth3;
    if (depth === 4) return classes.treeRowDepth4;
    if (depth === 5) return classes.treeRowDepth5;
    return classes.treeRowDepth6;
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2">Fields passed to the next agent.</Typography>
          <Tooltip title={labels?.helperText ?? 'Define output variables for the next agent'}>
            <HelpOutlineIcon className={classes.helpIcon} />
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <CodeIcon className={classes.codeIcon} />
          <Typography variant="caption">Preview JSON</Typography>
          <Switch
            checked={showJsonPreview}
            onChange={(_, checked) => setShowJsonPreview(checked)}
            size="small"
          />
        </Stack>
      </Stack>

      {showJsonPreview ? (
        <Paper variant="outlined" className={classes.jsonPreviewPaper}>
          <pre className={classes.jsonPreviewPre}>
            {JSON.stringify({ strict: true, schema }, null, 2)}
          </pre>
        </Paper>
      ) : (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          className={classes.editorSplit}>
          <Paper variant="outlined" className={classes.treePaper}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              className={classes.treeHeader}>
              <Typography variant="caption" color="text.secondary">
                Schema tree
              </Typography>
            </Stack>
            {Object.keys(schema.properties ?? {}).length === 0 ? (
              <Stack
                spacing={1.5}
                justifyContent="center"
                alignItems="center"
                className={classes.emptyTree}>
                <Typography variant="body2" color="text.secondary">
                  Define what the next agent should receive.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={disabled}
                  onClick={addRootField}>
                  Add first field
                </Button>
              </Stack>
            ) : (
              <Stack spacing={0.5}>
                {treeEntries.map(({ pathKey, name, node, depth }) => (
                  <SchemaTreeRow
                    key={pathKey}
                    pathKey={pathKey}
                    name={name}
                    node={node}
                    onSelect={setSelectedFieldPathKey}
                    getBadgeClass={getBadgeClass}
                    rowClassName={cx(
                      classes.treeRow,
                      getDepthClass(depth),
                      activeEntry?.pathKey === pathKey && classes.treeRowActive,
                    )}
                    contentClassName={classes.treeRowContent}
                    nameClassName={classes.treeName}
                  />
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  disabled={disabled}
                  className={classes.addRootButton}
                  onClick={addRootField}>
                  Add root field
                </Button>
              </Stack>
            )}
          </Paper>

          <Paper variant="outlined" className={classes.fieldPaper}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              className={classes.fieldHeader}>
              <Typography variant="caption" color="text.secondary">
                Field editor
              </Typography>
              <IconButton size="small" onClick={(event) => setShortcutAnchor(event.currentTarget)}>
                <HelpOutlineIcon className={classes.panelHelpIcon} />
              </IconButton>
            </Stack>
            <Typography variant="caption" color="text.secondary" className={classes.pathLabel}>
              {activeFieldPath?.length
                ? activeFieldPath
                    .map((segment) => (segment === ARRAY_ITEM_TOKEN ? 'item' : segment))
                    .join(' > ')
                : 'No field selected'}
            </Typography>
            {activeFieldName &&
              activeFieldPath?.[activeFieldPath.length - 1] !== ARRAY_ITEM_TOKEN && (
                <CustomTextField
                  fullWidth
                  size="small"
                  className={classes.variableInput}
                  label={labels?.variableNameHeader ?? 'Variable name'}
                  value={variableNameDraft}
                  disabled={disabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setVariableNameDraft(event.target.value)
                  }
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                    updateSelectedFieldName(event.target.value)
                  }
                />
              )}
            <Tooltip
              title={!canAddNestedField || disabled ? addNestedDisabledReason : ''}
              disableHoverListener={canAddNestedField && !disabled}>
              <Box component="span">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  disabled={disabled || !canAddNestedField}
                  className={classes.addNestedButton}
                  onClick={addNestedField}>
                  Add nested field
                </Button>
              </Box>
            </Tooltip>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={selectedType}
              className={classes.typeToggleGroup}>
              {TYPES.map((fieldType) => (
                <ToggleButton
                  key={fieldType}
                  value={fieldType}
                  disabled={disabled || !activeFieldName}
                  onClick={() => updateSelectedFieldType(fieldType)}>
                  {fieldType}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Accordion disableGutters elevation={0} className={classes.jsonAccordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">JSON for this field</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Box className={classes.jsonBox}>
                  <Tooltip title={copied ? 'Copied' : 'Copy JSON'}>
                    <IconButton
                      size="small"
                      className={classes.copyJsonButton}
                      onClick={async () => {
                        await navigator.clipboard.writeText(subtreeJson);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1000);
                      }}>
                      <ContentCopyIcon className={classes.copyIcon} />
                    </IconButton>
                  </Tooltip>
                  <pre
                    className={classes.jsonFieldPre}
                    dangerouslySetInnerHTML={{
                      __html: syntaxJson(subtreeJson, syntaxJsonColors),
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Stack>
      )}

      <Popover
        open={Boolean(shortcutAnchor)}
        anchorEl={shortcutAnchor}
        onClose={() => setShortcutAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Stack spacing={0.5} className={classes.shortcutsStack}>
          <Typography variant="subtitle2">Keyboard shortcuts</Typography>
          <Typography variant="caption">Enter - add sibling below</Typography>
          <Typography variant="caption">Tab - add child</Typography>
          <Typography variant="caption">Arrow up/down - navigate rows</Typography>
          <Typography variant="caption">Delete - remove selected field</Typography>
        </Stack>
      </Popover>
    </Stack>
  );
};
